import { useState, useEffect } from 'react';
import { X, CreditCard, AlertTriangle, CheckCircle, XCircle, Building2, Phone, Mail, MapPin, FileText, Clock, DollarSign } from 'lucide-react';
import { receptoresApi } from '../utils/api';
import useEscClose from '../hooks/useEscClose';

export default function CreditConfirmModal({
  receptorId,
  montoTotal,
  moneda = 'CRC',
  tipoCambio = 1,
  mode = 'credito', // 'credito' | 'contado' | 'facturar'
  onConfirm,
  onContado,
  onCredito,
  onCancel
}) {
  useEscClose(onCancel);

  const isContado = mode === 'contado';
  const isFacturar = mode === 'facturar';
  const [loading, setLoading] = useState(true);
  const [creditInfo, setCreditInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (receptorId) {
      loadCreditInfo();
    }
  }, [receptorId]);

  const loadCreditInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await receptoresApi.getCreditInfo(receptorId);
      if (response.data?.success) {
        setCreditInfo(response.data.data);
      } else {
        setError('Error al obtener información de crédito');
      }
    } catch (err) {
      console.error('Error loading credit info:', err);
      setError(err.response?.data?.error || 'Error al obtener información de crédito');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, curr = moneda) => {
    const symbol = curr === 'USD' ? '$' : '¢';
    return `${symbol}${parseFloat(amount || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR');
  };

  // El límite, el disponible y el saldo total van en colones; una factura en dólares se compara a su TC
  const montoCrc = moneda === 'USD' ? (parseFloat(montoTotal) || 0) * (parseFloat(tipoCambio) || 1) : (parseFloat(montoTotal) || 0);

  // Check if credit should be blocked
  const shouldBlockCredit = () => {
    if (!creditInfo?.credito) return false;
    const { credito } = creditInfo;

    // Block if credit is suspended
    if (credito.creditoSuspendido) return true;

    // Block if no credit account
    if (!credito.tieneCuentaCredito) return true;

    // Block if amount exceeds available credit (and there is a limit)
    if (!credito.creditoIlimitado && credito.creditoDisponible !== null) {
      if (montoCrc > credito.creditoDisponible) return true;
    }

    return false;
  };

  const getBlockReason = () => {
    if (!creditInfo?.credito) return '';
    const { credito } = creditInfo;

    if (credito.creditoSuspendido) return 'El crédito del cliente está suspendido';
    if (!credito.tieneCuentaCredito) return 'El cliente no tiene cuenta de crédito activa';
    if (!credito.creditoIlimitado && montoCrc > credito.creditoDisponible) {
      return `El monto (${formatCurrency(montoCrc, 'CRC')}) excede el crédito disponible (${formatCurrency(credito.creditoDisponible, 'CRC')})`;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 border-2 border-gray-200">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600 text-center">Verificando crédito...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-md border-2 border-gray-200">
          <div className="p-6">
            <div className="flex items-center gap-3 text-red-600 mb-4">
              <XCircle className="h-6 w-6" />
              <h2 className="text-lg font-semibold">Error</h2>
            </div>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { receptor, credito } = creditInfo;
  const isBlocked = (isContado || isFacturar) ? false : shouldBlockCredit();
  const blockReason = (isContado || isFacturar) ? '' : getBlockReason();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border-2 border-gray-200">
        {/* Header - Outline style */}
        <div className="flex items-center justify-between p-4 border-b-2 border-gray-200">
          <div className="flex items-center gap-3 text-gray-800">
            <div className="p-2 bg-blue-50 rounded-lg">
              {isFacturar ? <DollarSign className="h-5 w-5 text-green-600" /> : isContado ? <DollarSign className="h-5 w-5 text-green-600" /> : <CreditCard className="h-5 w-5 text-blue-600" />}
            </div>
            <h2 className="text-lg font-semibold">{isFacturar ? 'Facturar' : isContado ? 'Facturar de Contado' : 'Facturar a Crédito'}</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Monto Total */}
            <div className="border-2 border-gray-200 rounded-xl p-5">
              <div className="text-sm text-gray-500 mb-1">Monto Total:</div>
              <div className="text-3xl font-bold text-gray-800">
                {formatCurrency(montoTotal)}
              </div>
            </div>

            {/* Crédito Disponible */}
            <div className={`rounded-xl p-5 border-2 ${
              isBlocked
                ? 'border-red-300 bg-red-50/50'
                : credito.tieneFacturasVencidas
                  ? 'border-amber-300 bg-amber-50/50'
                  : 'border-green-300 bg-green-50/50'
            }`}>
              <div className={`text-sm mb-1 ${
                isBlocked ? 'text-red-600' : credito.tieneFacturasVencidas ? 'text-amber-600' : 'text-green-600'
              }`}>
                Crédito Disponible:
              </div>
              <div className={`text-3xl font-bold ${
                isBlocked ? 'text-red-700' : credito.tieneFacturasVencidas ? 'text-amber-700' : 'text-green-700'
              }`}>
                {credito.creditoIlimitado ? 'Ilimitado' : formatCurrency(credito.creditoDisponible, 'CRC')}
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Client Info */}
            <div className="border-2 border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
                <Building2 className="h-5 w-5 text-gray-500" />
                <span>{receptor.nombre}</span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-500">Identificación</div>
                    <div className="text-gray-900">{receptor.identificacion}</div>
                  </div>
                </div>

                {receptor.telefonoNumero && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">Teléfono</div>
                      <div className="text-gray-900">+{receptor.telefonoCodigo || '506'} {receptor.telefonoNumero}</div>
                    </div>
                  </div>
                )}

                {receptor.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="text-gray-900">{receptor.email}</div>
                    </div>
                  </div>
                )}

                {receptor.otrasSenas && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-gray-500">Dirección</div>
                      <div className="text-gray-900">{receptor.otrasSenas}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Credit Status */}
            <div className="border-2 border-gray-200 rounded-xl p-5">
              <div className="space-y-3">
                {/* Tiene cuenta de crédito */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Cliente tiene cuenta de crédito:</span>
                  <span className={`font-medium flex items-center gap-1 ${credito.tieneCuentaCredito ? 'text-green-600' : 'text-red-600'}`}>
                    {credito.tieneCuentaCredito ? (
                      <><CheckCircle className="h-4 w-4" /> Sí</>
                    ) : (
                      <><XCircle className="h-4 w-4" /> No</>
                    )}
                  </span>
                </div>

                {/* Crédito suspendido */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Cliente tiene crédito suspendido:</span>
                  <span className={`font-medium flex items-center gap-1 ${credito.creditoSuspendido ? 'text-red-600' : 'text-green-600'}`}>
                    {credito.creditoSuspendido ? (
                      <><XCircle className="h-4 w-4" /> Sí</>
                    ) : (
                      <><CheckCircle className="h-4 w-4" /> No</>
                    )}
                  </span>
                </div>

                {/* Facturas vencidas */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Cliente tiene facturas vencidas:</span>
                  <span className={`font-medium flex items-center gap-1 ${credito.tieneFacturasVencidas ? 'text-amber-600' : 'text-green-600'}`}>
                    {credito.tieneFacturasVencidas ? (
                      <><AlertTriangle className="h-4 w-4" /> Sí ({credito.cantidadFacturasVencidas})</>
                    ) : (
                      <><CheckCircle className="h-4 w-4" /> No</>
                    )}
                  </span>
                </div>

                {/* Límite de crédito */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Límite de crédito:</span>
                  <span className="font-medium text-gray-900">
                    {credito.creditoIlimitado ? 'Ilimitado' : formatCurrency(credito.limiteCredito, 'CRC')}
                  </span>
                </div>

                {/* Saldo pendiente */}
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Saldo pendiente actual:</span>
                  <span className={`font-medium ${credito.saldoPendiente > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                    {credito.saldoPorMoneda && Object.keys(credito.saldoPorMoneda).length
                      ? Object.entries(credito.saldoPorMoneda).map(([m, v]) => formatCurrency(v, m)).join(' · ')
                      : formatCurrency(credito.saldoPendiente, 'CRC')}
                  </span>
                </div>

                {/* Días de crédito */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Plazo de crédito:</span>
                  <span className="font-medium text-gray-900 flex items-center gap-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {receptor.diasCredito || 30} días
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Overdue Invoices Warning */}
          {credito.tieneFacturasVencidas && credito.facturasVencidas?.length > 0 && (
            <div className="mt-6 bg-amber-50/50 border-2 border-amber-300 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-3">
                <AlertTriangle className="h-5 w-5" />
                <span>Facturas Vencidas</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-amber-600">
                      <th className="pb-2">Consecutivo</th>
                      <th className="pb-2">Fecha</th>
                      <th className="pb-2">Vencimiento</th>
                      <th className="pb-2 text-right">Saldo</th>
                      <th className="pb-2 text-right">Días vencido</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    {credito.facturasVencidas.map((f) => (
                      <tr key={f.id} className="border-t border-amber-200">
                        <td className="py-2">{f.consecutivo}</td>
                        <td className="py-2">{formatDate(f.fechaEmision)}</td>
                        <td className="py-2">{formatDate(f.fechaVencimiento)}</td>
                        <td className="py-2 text-right">{formatCurrency(f.saldoPendiente, f.moneda)}</td>
                        <td className="py-2 text-right text-red-600 font-medium">{f.diasVencido}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Block Warning */}
          {isBlocked && (
            <div className="mt-6 bg-red-50/50 border-2 border-red-300 rounded-xl p-4">
              <div className="flex items-center gap-3 text-red-700">
                <XCircle className="h-6 w-6 flex-shrink-0" />
                <div>
                  <div className="font-semibold">No se puede aplicar crédito</div>
                  <div className="text-sm text-red-600">{blockReason}</div>
                </div>
              </div>
            </div>
          )}

          {/* Credit Notes */}
          {credito.notasCredito && (
            <div className="mt-6 border-2 border-gray-200 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Notas de crédito:</div>
              <div className="text-gray-700">{credito.notasCredito}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t-2 border-gray-200">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
          >
            Cancelar
          </button>
          {isFacturar ? (
            <>
              <button
                onClick={onContado}
                className="px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 border-2 bg-white border-green-500 text-green-600 hover:bg-green-50"
              >
                <DollarSign className="h-4 w-4" />
                Facturar de Contado
              </button>
              <button
                onClick={onCredito}
                className="px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 border-2 bg-white border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <CreditCard className="h-4 w-4" />
                Facturar a Crédito
              </button>
            </>
          ) : (
            <button
              onClick={onConfirm}
              disabled={isBlocked}
              className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 border-2 ${
                isBlocked
                  ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-green-500 text-green-600 hover:bg-green-50'
              }`}
            >
              {isContado ? <DollarSign className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
              {isContado ? 'Facturar de Contado' : 'Aplicar Factura a Crédito'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
